import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  CardBody,
  Card,
  Container,
  Input,
  Label,
  Form,
  FormFeedback,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import logolight from "../../assets/images/logo-light.png";
import logodark from "../../assets/images/logo-dark.png";
import PokemonReveal from "../../components/PokemonReveal/PokemonReveal";
import api from "../../api";

const starterOptions = ["Bulbasaur", "Charmander", "Squirtle"];

const Register = () => {
  document.title = "Register | Pokémon Radiance";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [popup, setPopup] = useState({ show: false, message: "", type: "" });
  const [showTermsModal, setShowTermsModal] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      username: "",
      password: "",
      confirmPassword: "",
      policy: false,
      starter: "",
      referredBy: "", // New field for referral
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Must be a valid email")
        .max(255)
        .required("Please Enter Your Email"),
      username: Yup.string()
        .min(4, "Minimum of 4 characters")
        .max(15, "Maximum of 15 characters")
        .matches(/^[a-zA-Z0-9]+$/, "Username can only contain letters and numbers")
        .required("Username is required"),
      password: Yup.string()
        .min(7, "Minimum of 7 characters")
        .max(20, "Maximum of 20 characters")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(
          /(?=.*[!@#$%^&*()\-_=+{};:,<.>])/,
          "Password must contain at least one special character"
        )
        .required("Password is required"),
      confirmPassword: Yup.string()
        .max(255)
        .required("Password confirmation is required")
        .oneOf([Yup.ref("password"), null], "Passwords must match"),
      policy: Yup.boolean().oneOf(
        [true],
        "You must accept the terms and conditions"
      ),
      starter: Yup.string().required("Please choose a starter"),
    }),
    onSubmit: (values) => {
      const registerRequest = async () => {
        try {
          const resp = await api.post(
            "/v1/register",
            {
              email: values.email,
              username: values.username,
              password: values.password,
              policy: values.policy,
              starter: values.starter,
              referredBy: values.referredBy // Include referredBy field with correct name
            },
            { withCredentials: true }
          );

          if (resp.data.message === "Account Created!") {
            setPopup({
              show: true,
              message: "Account Created! Redirecting to Login...",
              type: "success",
            });
            // Redirect after 3 seconds
            setTimeout(() => {
              navigate("/login");
            }, 3000);
          } else {
            // Handle unexpected response
            setPopup({
              show: true,
              message: "Registration completed but received unexpected response. Please try logging in.",
              type: "warning",
            });
            setTimeout(() => {
              navigate("/login");
            }, 3000);
          }
        } catch (err) {
          console.error('Registration error:', err);
          
          // Handle different types of errors
          let errorMessage = "Registration failed.";
          
          if (err.response) {
            // Server responded with error status
            errorMessage = err.response.data?.detail || `Server error: ${err.response.status}`;
          } else if (err.request) {
            // Network error - no response received
            errorMessage = "Network error. Please check your connection and try again.";
          } else {
            // Other error
            errorMessage = err.message || "An unexpected error occurred.";
          }
          
          setPopup({
            show: true,
            message: errorMessage,
            type: "error",
          });
          
          // Hide after 5 seconds to give user time to read
          setTimeout(() => {
            setPopup({ show: false, message: "", type: "" });
          }, 5000);
        }
      };
      registerRequest();
    },
  });

  useEffect(() => {
    dispatch({ type: "API_ERROR", payload: "" });
  }, [dispatch]);

  useEffect(() => {
    document.body.className = "bg-pattern";
    return function cleanup() {
      document.body.className = "";
    };
  }, []);

  const handleTermsAccept = () => {
    validation.setFieldValue("policy", true);
    setShowTermsModal(false);
  };

  return (
    <div className="bg-overlay">
      <div className="account-pages my-5 pt-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8} xl={4}>
              {popup.show && (
                <Alert
                  color={popup.type === "success" ? "success" : "danger"}
                  fade
                  className="position-fixed top-0 start-50 translate-middle-x mt-3 shadow"
                  style={{ zIndex: 1060, width: "auto", minWidth: 300 }}
                >
                  {popup.type === "success" ? (
                    <i className="mdi mdi-check-circle-outline me-2"></i>
                  ) : (
                    <i className="mdi mdi-alert-outline me-2"></i>
                  )}
                  {popup.message}
                </Alert>
              )}
              <Card>
                <CardBody className="p-4">
                  <div className="text-center">
                    <Link to="/">
                      <img
                        src={logodark}
                        alt=""
                        height="24"
                        className="auth-logo logo-dark mx-auto"
                      />
                      <img
                        src={logolight}
                        alt=""
                        height="24"
                        className="auth-logo logo-light mx-auto"
                      />
                    </Link>
                  </div>
                  <h4 className="font-size-18 text-muted text-center mt-2">
                    Free Register
                  </h4>
                  <p className="text-center mb-5 text-muted">
                    Start your adventure by creating an account.
                  </p>
                  <Form
                    className="form-horizontal"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <Row>
                      <Col md={12}>
                        <div className="mb-4">
                          <Label className="form-label">Email</Label>
                          <Input
                            id="email"
                            name="email"
                            className="form-control"
                            placeholder="Enter email"
                            type="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email}
                            invalid={
                              validation.touched.email &&
                              !!validation.errors.email
                            }
                          />
                          {validation.touched.email &&
                            validation.errors.email && (
                              <FormFeedback type="invalid">
                                {validation.errors.email}
                              </FormFeedback>
                            )}
                        </div>
                        <div className="mb-4">
                          <Label className="form-label">Username</Label>
                          <Input
                            id="username"
                            name="username"
                            className="form-control"
                            type="text"
                            placeholder="Enter username"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.username}
                            invalid={
                              validation.touched.username &&
                              !!validation.errors.username
                            }
                          />
                          {validation.touched.username &&
                            validation.errors.username && (
                              <FormFeedback type="invalid">
                                {validation.errors.username}
                              </FormFeedback>
                            )}
                        </div>
                        
                        <div className="mb-4">
                          <Label className="form-label">Referred by (Optional)</Label>
                          <Input
                            id="referredBy"
                            name="referredBy"
                            className="form-control"
                            type="text"
                            placeholder="Enter username of who referred you"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.referredBy || ""}
                          />
                        </div>
                        <div className="mb-4">
                          <Label className="form-label">Password</Label>
                          <Input
                            name="password"
                            type="password"
                            placeholder="Enter Password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password}
                            invalid={
                              validation.touched.password &&
                              !!validation.errors.password
                            }
                          />
                          {validation.touched.password &&
                            validation.errors.password && (
                              <FormFeedback type="invalid">
                                {validation.errors.password}
                              </FormFeedback>
                            )}
                        </div>
                        <div className="mb-4">
                          <Label className="form-label">
                            Confirm Password
                          </Label>
                          <Input
                            name="confirmPassword"
                            type="password"
                            placeholder="Confirm Password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.confirmPassword}
                            invalid={
                              validation.touched.confirmPassword &&
                              !!validation.errors.confirmPassword
                            }
                          />
                          {validation.touched.confirmPassword &&
                            validation.errors.confirmPassword && (
                              <FormFeedback type="invalid">
                                {validation.errors.confirmPassword}
                              </FormFeedback>
                            )}
                        </div>
                        <div className="mb-4">
                          <Label className="form-label">
                            Choose Your Starter
                          </Label>
                          <Row>
                            {starterOptions.map((starter) => (
                              <Col xs="4" key={starter}>
                                <div
                                  className={`starter-option ${
                                    validation.values.starter === starter
                                      ? "selected"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    validation.setFieldValue("starter", starter)
                                  }
                                  style={{
                                    cursor: "pointer",
                                    border:
                                      validation.values.starter === starter
                                        ? "2px solid #0bb197"
                                        : "1px solid #ddd",
                                    borderRadius: "8px",
                                    padding: "5px",
                                    marginBottom: "10px",
                                    textAlign: "center",
                                  }}
                                >
                                  <PokemonReveal pokemon={starter.toLowerCase()} />
                                  <p
                                    className="small"
                                    style={{ margin: "5px 0 0" }}
                                  >
                                    {starter}
                                  </p>
                                </div>
                              </Col>
                            ))}
                          </Row>
                          {validation.touched.starter &&
                            validation.errors.starter && (
                              <FormFeedback className="d-block">
                                {validation.errors.starter}
                              </FormFeedback>
                            )}
                        </div>
                        <div className="form-check mb-4">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="term-conditionCheck"
                            name="policy"
                            onChange={validation.handleChange}
                            checked={validation.values.policy}
                          />
                          <label
                            className="form-check-label fw-normal"
                            htmlFor="term-conditionCheck"
                          >
                            I accept{" "}
                            <span
                              onClick={() => setShowTermsModal(true)}
                              style={{ cursor: "pointer" }}
                              className="text-primary"
                            >
                              Terms and Conditions
                            </span>
                          </label>
                          {validation.touched.policy &&
                            validation.errors.policy && (
                              <FormFeedback className="d-block">
                                {validation.errors.policy}
                              </FormFeedback>
                            )}
                        </div>
                        <div className="d-grid mt-4">
                          <button
                            className="btn btn-danger waves-effect waves-light"
                            type="submit"
                          >
                            Register
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p className="text-white-50">
                  Already have an account?{" "}
                  <Link to="/login" className="fw-medium text-primary">
                    Login
                  </Link>
                </p>
                <p className="text-white-50">
                  © 1995–{new Date().getFullYear()}, GAME FREAK Inc., Creatures
                  Inc., Nintendo. No commerce activities take place on this
                  website.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Terms and Conditions Modal */}
      <Modal
        isOpen={showTermsModal}
        toggle={() => setShowTermsModal(!showTermsModal)}
        size="lg"
        scrollable
        fullscreen="sm"
      >
        <ModalHeader toggle={() => setShowTermsModal(!showTermsModal)}>
          Terms and Conditions
        </ModalHeader>
        <ModalBody>
          <p>
            <strong>Pokémon Radiance </strong> is an online Pokémon RPG website
            designed to provide the ultimate gaming experience while supporting
            various charities.
          </p>
          <p>
            <strong>Disclaimer:</strong> The online Pokémon game,{" "}
            <strong>Pokémon Radiance</strong>, is not associated with, endorsed
            by, or sponsored by Gamefreak, Nintendo, Nintendo of Japan, Nintendo
            of America, Creatures, or any other company that owns the rights to
            Pokémon.
          </p>
          <p>
            All Pokémon, Pokémon data, Pokémon images, and any other related
            names used on <strong>Pokémon Radiance</strong> are the copyrighted
            property of Nintendo, Gamefreak, Creatures, and their respective
            owners.
          </p>
          <p>
            The majority of the server-side programming, coding, and design of
            this site is developed by Brokeboy.
          </p>
          <p>
            <strong>No trademark or copyright infringement is intended.</strong>{" "}
            We strongly urge all players to purchase the official Pokémon games
            and merchandise to support the creators.
          </p>
          <p>
            <strong>Non-Profit, Fan-Made Project & Credits:</strong> This
            project is a non-commercial, fan-made remake created purely for fun
            and to pay homage to the original <strong>Pokemon Creed</strong>{" "}
            game, which has not been updated. It is built using React and
            Python, and all proceeds or in-game acquisitions (such as coins and
            promos) are solely for enhancing the gameplay experience without any
            profit motive.
          </p>
          <p>
            <strong>Additional Indemnification:</strong> By accessing and using
            this website, you acknowledge and agree that the creators,
            developers, and administrators of this site are not liable for any
            claims, damages, or legal actions arising from your use of the site.
            You agree to indemnify and hold harmless the site’s operators from
            any and all claims or damages resulting from your actions or
            reliance on the information provided.
          </p>
          <p>
            The content on this site is provided "as is" without any warranty,
            either express or implied. We make no representations or warranties
            regarding the accuracy, reliability, or legality of the information
            contained herein.
          </p>
          <p>
            By clicking "I Agree", you confirm that you have read, understood,
            and accepted these terms and conditions.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowTermsModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleTermsAccept}>
            I Agree
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Register;
